import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import LoadingSpinner from '../components/shared/LoadingSpinner/LoadingSpinner';
import AdminRoute from './AdminRoute';
import UserProtectedRoute from './UserProtectedRoute';

// User routes (lazy loaded)
const UserHome = lazy(() => import('../pages/user/Home/Home'));
const UserProductList = lazy(() => import('../pages/user/Products/ProductList/ProductList'));
const UserProductDetails = lazy(() => import('../pages/user/Products/ProductDetails/ProductDetails'));
const Cart = lazy(() => import('../pages/user/Cart/Cart'));
const UserSignIn = lazy(() => import('../pages/user/Auth/UserSignIn/UserSignIn'));
const UserRegister = lazy(() => import('../pages/user/Auth/UserRegister/UserRegister'));
const ForgotPassword = lazy(() => import('../pages/user/Auth/ForgotPassword/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/user/Auth/ResetPassword/ResetPassword'));
const Checkout = lazy(() => import('../pages/user/Checkout/Checkout'));
const CheckoutMethod = lazy(() => import('../pages/user/CheckoutMethod/CheckoutMethod'));
const OrderConfirmation = lazy(() => import('../pages/user/OrderConfirmation/OrderConfirmation'));
const MyOrders = lazy(() => import('../pages/user/MyOrders/MyOrders'));
const OrderDetails = lazy(() => import('../pages/user/OrderDetails/OrderDetails'));
const UserProfile = lazy(() => import('../pages/user/UserProfile/UserProfile'));

// Admin routes (lazy loaded - won't load in user bundle)
const AdminSignIn = lazy(() => import('../pages/admin/SignIn/SignIn'));
const AdminCategories = lazy(() => import('../pages/admin/Categories/Categories'));
const AdminOrders = lazy(() => import('../pages/admin/Orders/Orders'));
const AdminOrderDetails = lazy(() => import('../pages/admin/OrderDetails/OrderDetails'));
const DeliveryFeeSettings = lazy(() => import('../pages/admin/Settings/DeliveryFeeSettings/DeliveryFeeSettings'));
const AdminUsers = lazy(() => import('../pages/admin/Users/AdminUsers'));
const AdminProductDetails = lazy(() => import('../pages/admin/Products/ProductDetails/ProductDetails'));
const AddProduct = lazy(() => import('../pages/admin/Products/AddProduct/AddProduct'));
const EditProduct = lazy(() => import('../pages/admin/Products/EditProduct/EditProduct'));

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public User Routes */}
        <Route path="/" element={<UserHome />} />
        <Route path="/products" element={<UserProductList />} />
        <Route path="/products/:slug" element={<UserProductDetails />} />
        <Route path="/cart" element={<Cart />} />

        {/* User Auth Routes */}
        <Route path="/sign-in" element={<UserSignIn />} />
        <Route path="/register" element={<UserRegister />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />

        {/* Checkout Flow (accessible to both guests and registered users) */}
        <Route path="/checkout-method" element={<CheckoutMethod />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-confirmation" element={<OrderConfirmation />} />

        {/* Protected User Routes */}
        <Route
          path="/my-orders"
          element={<UserProtectedRoute><MyOrders /></UserProtectedRoute>}
        />
        <Route
          path="/my-orders/:trackingCode"
          element={<UserProtectedRoute><OrderDetails /></UserProtectedRoute>}
        />
        <Route
          path="/profile"
          element={<UserProtectedRoute><UserProfile /></UserProtectedRoute>}
        />

        {/* Admin Routes - All under /admin prefix */}
        <Route path="/admin/sign-in" element={<AdminSignIn />} />
        <Route
          path="/admin/categories"
          element={<AdminRoute><AdminCategories /></AdminRoute>}
        />
        <Route
          path="/admin/orders"
          element={<AdminRoute><AdminOrders /></AdminRoute>}
        />
        <Route
          path="/admin/orders/:trackingCode"
          element={<AdminRoute><AdminOrderDetails /></AdminRoute>}
        />
        <Route
          path="/admin/settings/delivery-fee"
          element={<AdminRoute><DeliveryFeeSettings /></AdminRoute>}
        />
        <Route
          path="/admin/users"
          element={<AdminRoute><AdminUsers /></AdminRoute>}
        />
        <Route
          path="/admin/products/:id"
          element={<AdminRoute><AdminProductDetails /></AdminRoute>}
        />
        <Route
          path="/admin/products/add/:categoryId"
          element={<AdminRoute><AddProduct /></AdminRoute>}
        />
        <Route
          path="/admin/products/edit/:productId"
          element={<AdminRoute><EditProduct /></AdminRoute>}
        />

        {/* Redirects */}
        <Route path="/admin" element={<Navigate to="/admin/sign-in" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
